/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Image as KonvaImage } from 'react-konva';
import { SvgElement } from '../../../core/template.schemas';
import { ResolveContext, resolveImageSource, resolveStaticOrDynamicWithPreview } from '../../../core/runtime/resolve';
import { useCacheBustedImage } from '../../../hooks/useCacheBustedImage';
import { processSvg } from '../../../utils/svgUtils';

interface SvgRendererProps {
  element: SvgElement;
  resolveContext: ResolveContext;
  eventHandlers: any;
  config?: any;
}

export const SvgRenderer: React.FC<SvgRendererProps> = ({ element, resolveContext, eventHandlers, config }) => {
  const [svgImage, setSvgImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Combine config with basic props
  const finalConfig = useMemo(() => {
    return {
      ...config,
      id: element.id,
      x: config?.x ?? element.x,
      y: config?.y ?? element.y,
      width: element.width,
      height: element.height,
      rotation: config?.rotation ?? element.rotation,
      opacity: config?.opacity ?? element.opacity,
      visible: element.visible,
      draggable: !element.locked,
      ...eventHandlers,
    };
  }, [element, config, eventHandlers]);

  // 1. Resolve the source URL
  const src = useMemo(() => {
    return resolveImageSource(element.source, resolveContext);
  }, [element.source, resolveContext]);

  const [cachedImage, cachedStatus] = useCacheBustedImage(src || '', 'anonymous');

  // 2. Resolve color mappings
  const resolvedColors = useMemo(() => {
    const mappings: Record<string, string> = {};
    if (element.colorMapping) {
      Object.entries(element.colorMapping).forEach(([originalColor, value]) => {
        const resolved = resolveStaticOrDynamicWithPreview(value, resolveContext);
        if (resolved) {
          mappings[originalColor.toLowerCase()] = resolved;
        }
      });
    }
    return mappings;
  }, [element.colorMapping, resolveContext]);

  const monochromeColor = useMemo(() => {
    if (element.fillMode === 'monochrome' && element.monochromeColor) {
      return resolveStaticOrDynamicWithPreview(element.monochromeColor, resolveContext);
    }
    return null;
  }, [element.fillMode, element.monochromeColor, resolveContext]);

  // 3. Fetch, process, and create Image from SVG using cached blob URL and shared svgUtils
  useEffect(() => {
    if (!src) {
      setSvgImage(null);
      return;
    }

    if (cachedStatus !== 'loaded' || !cachedImage) {
      return;
    }

    let isActive = true;

    const loadSvg = async () => {
      try {
        const response = await fetch(cachedImage.src);
        if (!response.ok) throw new Error('Failed to fetch SVG from blob URL');
        const blob = await response.blob();
        const text = await blob.text();

        if (!isActive) return;

        const processedSvg = processSvg(text, {
          fillMode: element.fillMode || 'original',
          monochromeColor: monochromeColor || undefined,
          colorMapping: resolvedColors,
        });

        const svgBlob = new Blob([processedSvg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
          if (isActive) {
            setSvgImage(img);
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => {
          if (isActive) setError('Failed to render SVG image');
          URL.revokeObjectURL(url);
        };
        img.src = url;
      } catch (err) {
        if (isActive) setError((err as Error).message);
      }
    };

    loadSvg();

    return () => {
      isActive = false;
    };
  }, [src, resolvedColors, monochromeColor, element.fillMode, cachedImage, cachedStatus]);

  if (!svgImage) {
      // Placeholder or loading state
      return (
          <KonvaImage
              {...finalConfig}
              image={undefined}
              fill="#E0E0E0" // Placeholder gray
          />
      );
  }

  return (
    <KonvaImage
      {...finalConfig}
      image={svgImage}
    />
  );
};
